import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";

export default function Footer() {
  const { t } = useI18n();
  const [zones, setZones] = useState([]);
  const [info, setInfo] = useState({ support_phone: "", company_phone: "", support_email: "", hours: "", days_open: "" });

  useEffect(() => {
    api.get("/zones").then((r) => setZones(unwrap(r) || [])).catch(() => {});
    api.get("/public/settings").then((r) => {
      const d = unwrap(r) || {};
      setInfo({
        support_phone: d.support_phone || "",
        company_phone: d.company_phone || "",
        support_email: d.support_email || "",
        hours: d.hours || "",
        days_open: d.days_open || "",
      });
    }).catch(() => {});
  }, []);

  return (
    <footer className="bg-[#0C1A1A] text-white mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-5 gap-8">
        <div className="md:col-span-2">
          <div className="bg-white rounded-xl px-4 py-3 w-fit">
            <img src="/brand/logo-everclean-wordmark.png" alt="Ever Clean Home" className="h-14 md:h-16 w-auto object-contain" data-testid="footer-logo-img" />
          </div>
          <p className="mt-3 text-sm text-white/60 max-w-sm">{t("footer.tagline")} — {t("footer.description")}</p>
          <Link to="/book" className="inline-flex mt-5 items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FACC15] text-[#0C1A1A] font-semibold hover:scale-[1.02] transition-transform">
            {t("footer.book")} →
          </Link>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-white/80 mb-4">{t("footer.contact")}</h4>
          <ul className="space-y-3 text-sm text-white/70">
            {info.support_email && (
              <li className="flex items-center gap-2" data-testid="footer-email"><Mail className="w-4 h-4" /> {info.support_email}</li>
            )}
            {(info.company_phone || info.support_phone) && (
              <li data-testid="footer-phone">
                <a href={`tel:${(info.company_phone || info.support_phone).replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 hover:text-[#FACC15]">
                  <Phone className="w-4 h-4" /> {info.company_phone || info.support_phone}
                </a>
              </li>
            )}
            {(info.hours || info.days_open) && (
              <li className="flex items-start gap-2" data-testid="footer-hours">
                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {info.days_open && <span className="block">{info.days_open}</span>}
                  {info.hours && <span className="block text-white/55">{info.hours}</span>}
                </span>
              </li>
            )}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-white/80 mb-4">{t("footer.service_areas")}</h4>
          <ul className="space-y-2 text-sm text-white/70">
            {zones.map((z) => (
              <li key={z.id}>
                <Link to={`/cleaning-services/${z.slug || z.name.toLowerCase().replace(/, /g, "-").replace(/ /g, "-")}`}
                  className="hover:text-[#FACC15]" data-testid={`footer-zone-${z.state}`}>
                  {z.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-white/80 mb-4">{t("footer.legal")}</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li><Link to="/about" className="hover:text-[#FACC15]">{t("footer.about")}</Link></li>
            <li><Link to="/for-hosts" className="hover:text-[#FACC15]" data-testid="footer-for-hosts">{t("hosts.nav")}</Link></li>
            <li><Link to="/work-with-us" className="hover:text-[#FACC15]" data-testid="footer-work-with-us">{t("footer.work_with_us")}</Link></li>
            <li><Link to="/track" className="hover:text-[#FACC15]">{t("footer.track")}</Link></li>
            <li><Link to="/terms" className="hover:text-[#FACC15]">{t("footer.terms")}</Link></li>
            <li><Link to="/privacy" className="hover:text-[#FACC15]">{t("footer.privacy")}</Link></li>
            <li><Link to="/sms" className="hover:text-[#FACC15]" data-testid="footer-sms-program">{t("footer.sms")}</Link></li>
            <li><Link to="/faq" className="hover:text-[#FACC15]">{t("nav.faq")}</Link></li>
            <li><Link to="/admin/login" className="hover:text-[#FACC15]">{t("footer.admin")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 text-xs text-white/40 flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} Ever Clean Home. {t("footer.rights")}</span>
          <span className="font-mono">Boston · Cambridge · Providence · Hartford</span>
        </div>
      </div>
    </footer>
  );
}
